import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { GENDER } from '@feature/dashboard/senders/constant/upsert-sender.constant';
import { ROLE_TYPE } from '@feature/dashboard/user-management/roles/constant/upsert-role.constant';
import { RoleListInterface } from '@feature/dashboard/user-management/roles/types/role-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Country } from '@sharedModule/models/country.model';
import { FormService } from '@sharedModule/services/form/form.service';
import { selectCountry } from '@sharedModule/store/country/country.selector';
import { getCountryInfoFromAlpha3 } from '@sharedModule/utils/array.utils';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { Utils } from '@sharedModule/utils/util';
import { Observable, Subject, takeUntil } from 'rxjs';
import { BOOLEAN_OPTION } from '../../constant/upsert-user.constant';
import { UserService } from '../../services/user.service';
import { AddUserInterface, UpdateUserInterface } from '../../types/upsert-user';
import { UserListInterface } from '../../types/user-list';

@Component({
  selector: 'app-upsert-user',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './upsert-user.component.html',
  styleUrl: './upsert-user.component.scss',
})
export class UpsertUserComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.users;
  public readonly roleType = ROLE_TYPE;
  public readonly gender = GENDER;
  public readonly booleanOption = BOOLEAN_OPTION;
  upsertUserFrom!: FormGroup;
  countryList!: Observable<Country[]>;
  roleList!: RoleListInterface[];
  isEdit = false;
  userDetails!: UserListInterface;
  partnerList!: Observable<PartnerListInterface[]>;
  #destroy$ = new Subject<void>();

  constructor(
    private utils: Utils,
    private store: Store,
    private router: Router,
    private userService: UserService,
    private formService: FormService,
    private partnerService: PartnerService,
    private messageHandlerService: MessageHandlerService
  ) {}

  ngOnInit(): void {
    this.upsertUserFromInit();
    this.partnerList = this.partnerService.sharedData$;
    this.countryList = this.store.select(selectCountry);
    this.isEdit = this._isEdit(this.router.url);
  }

  upsertUserFromInit(): void {
    this.upsertUserFrom = new FormGroup({
      [this.attrConstant.userType.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.partner.key]: new FormControl(
        { value: '', disabled: true },
        [Validators.required]
      ),
      [this.attrConstant.firstName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.lastName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.nationality.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.dob.key]: new FormControl(''),
      [this.attrConstant.gender.key]: new FormControl(''),
      [this.attrConstant.email.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(REGEX_CONSTANT.email),
      ]),
      [this.attrConstant.countryCode.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.mobileNumber.key]: new FormControl('', [
        Validators.pattern(/^[0-9]{7,10}$/),
      ]),
      [this.attrConstant.roleId.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.password.key]: new FormControl(
        '',
        !this.isEdit ? [Validators.required] : []
      ),
      [this.attrConstant.address1.key]: new FormControl(''),
      [this.attrConstant.address2.key]: new FormControl(''),
      [this.attrConstant.city.key]: new FormControl(''),
      [this.attrConstant.postalCode.key]: new FormControl(''),
      [this.attrConstant.isMFADisabled.key]: new FormControl(false, [
        Validators.required,
      ]),
    });
  }

  onUserTypeSelected(event: { value: string }): void {
    this.userService
      .getRoleListDropdown({ role_type: event.value })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success)
            this.roleList = x.response.map((data: RoleListInterface) => {
              return Object.assign(data, {
                name_description:
                  data.description !== ''
                    ? `${data.name} - ${data.description}`
                    : data.name,
              });
            });
        },
      });
    if (!this.isEdit) {
      if (event.value === 'PLATFORM')
        this.formService.disableControls(this.upsertUserFrom, [
          this.attrConstant.partner.key,
        ]);
      else
        this.formService.enableControls(this.upsertUserFrom, [
          this.attrConstant.partner.key,
        ]);
    }
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[3] == 'edit') {
      this.formService.disableControls(this.upsertUserFrom, [
        this.attrConstant.userType.key,
        this.attrConstant.partner.key,
        this.attrConstant.password.key,
      ]);
      this.getUserDetails(endUrl[4]);
      return true;
    } else {
      return false;
    }
  };

  getUserDetails(data: string): void {
    const payload = {
      user_id: data.split('-')[0],
      partner_id: +data.split('-')[1],
    };
    this.userService
      .getUserDetails(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.userDetails = x.response;
            this.onUserTypeSelected({ value: x.response.role_type });
            this.upsertUserFrom.patchValue(x.response);
            this.upsertUserFrom.patchValue({
              [this.attrConstant.dob.key]:
                x.response.date_of_birth !== ''
                  ? new Date(this.utils.convertDate(x.response.date_of_birth))
                  : '',
            });
          }
        },
      });
  }

  onNationalitySelected(event: {
    value: string;
    selectOptions: Country[] | null;
  }): void {
    const selectedSourceCountry = getCountryInfoFromAlpha3(
      event.value,
      event.selectOptions
    );
    if (selectedSourceCountry)
      this.upsertUserFrom.patchValue({
        [this.attrConstant.countryCode.key]:
          selectedSourceCountry.telephone_code,
      });
  }

  patchPassword(password: string): void {
    this.upsertUserFrom.patchValue({
      [this.attrConstant.password.key]: password,
    });
  }

  onUpsertUser(): void {
    showValidationMsg(this.upsertUserFrom);
    if (this.upsertUserFrom.valid) {
      // delete this.upsertUserFrom.value.role_type;
      const formValue = this.upsertUserFrom.value;
      formValue[this.attrConstant.dob.key] = this.utils.convertDateFormat(
        formValue[this.attrConstant.dob.key]
      );
      if (this.isEdit)
        this.updateUser({
          partner_id: this.userDetails.partner_id,
          user_id: this.userDetails.user_id,
          ...formValue,
        });
      else
        this.addUser({
          partner_id: 0,
          is_super_admin:
            formValue[this.attrConstant.userType.key] === 'PLATFORM',
          ...formValue,
        });
    }
  }

  addUser(formValue: AddUserInterface): void {
    this.userService
      .addUser(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/user-management/user/list']);
          }
        },
      });
  }

  updateUser(formValue: UpdateUserInterface): void {
    this.userService
      .updateUser(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/user-management/user/list']);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
